import React, { useContext, useEffect, useRef, useState } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';
import { analyticsTrack } from '@sky-it-video/hlroom-analytics';

import Player from '../../atoms/player/player/Player';
import styles from '../../../utils/styles';
import { IconStatus } from '../../../typings/design-tokens';
import { IPlayerSetup } from '../../../typings/hlroom';
import { CategoryType } from '../../atoms/player/category/Category';
import { VideoInfoStatusType } from '../../atoms/player/video-info-status/VideoInfoStatus';
import './PlayerCard.scss';
import { PlayerContext } from '../../../context/player/PlayerProvider';
import PosterPlayer from '../poster-player/PosterPlayer';
import StickyPlayerHeader from '../sticky-player-header/StickyPlayerHeader';

export interface IPlayerCard {
  assetId: string | number;
  duration?: string;
  videoStill?: string;
  altImage?: string;
  playButtonState?: IconStatus;
  onSetCategoryType: (type: CategoryType) => void;
  onSetVideoStatusType: (type: VideoInfoStatusType) => void;
  onSetIsHiddenVideoInfoStatus: (isHiddenVideoInfoStatus: boolean) => void;
  title: string;
  isDesktop: boolean;
  navFilterallVisibility?: boolean;
  isLiveVideo?: boolean;
}

const PlayerCard: React.FC<IPlayerCard> = ({
  assetId,
  duration,
  videoStill,
  altImage = '',
  playButtonState,
  onSetCategoryType,
  onSetVideoStatusType,
  onSetIsHiddenVideoInfoStatus,
  title,
  isDesktop,
  navFilterallVisibility,
  isLiveVideo,
}) => {
  const { loaded, error, manager, posterClicked, onPosterClicked, activePlayer, playerMute, onTogglePlayerMute } =
    useContext(PlayerContext);
  const [playerReady, setPlayerReady] = useState<boolean>(false);

  const [isHiddenPoster, setIsHiddenPoster] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);
  const [eventName, setEventName] = useState<string>('unknown');
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [showCloseBtn, setShowCloseBtn] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const entry = useIntersectionObserver(ref, { threshold: 0.5, rootMargin: '-100px 0px' });

  useEffect(() => {
    if (assetId && entry?.isIntersecting && activePlayer === assetId.toString()) {
      setIsSticky(false);
    } else if (assetId && !entry?.isIntersecting && activePlayer === assetId.toString()) {
      setIsSticky(true);
    } else if (activePlayer !== assetId.toString()) {
      setIsSticky(false);
    }
  }, [entry, activePlayer, assetId]);

  const handleClick = (assetId: string) => {
    setIsHiddenPoster(true);
    onPosterClicked(assetId);
  };
  useEffect(() => {
    if (loaded && !error && manager) {
      if (isHiddenPoster) {
        setPlayerReady(true);
      } else if (!isHiddenPoster) {
        setPlayerReady(false);
      }
    }
    return () => {
      setPlayerReady(false);
    };
  }, [error, loaded, manager, isHiddenPoster]);
  useEffect(() => {
    if (assetId && posterClicked) {
      if (posterClicked !== assetId.toString()) {
        setIsHiddenPoster(false);
        // set mute false for player not live stream
        if (posterClicked === '1') {
          onTogglePlayerMute(false);
        }
      }
    }
  }, [posterClicked, assetId, navFilterallVisibility, isSticky]);

  // Live autostart and mute setup
  useEffect(() => {
    if (assetId === 1 && isLiveVideo) {
      handleClick(assetId.toString());
      onTogglePlayerMute(true);
    }
  }, []);

  const onShow = () => {
    analyticsTrack('adv_reward');
  };
  const onAccept = () => {
    analyticsTrack('adv_reward_accetta');
  };
  const onReject = () => {
    analyticsTrack('adv_reward_rifiuta');
  };

  const autostart = true;

  const liveSetup: { live?: boolean; daiOff?: boolean; mute?: boolean } = {};
  if (isLiveVideo) {
    liveSetup.live = true;
    liveSetup.mute = playerMute;
  }
  const playerSetup: IPlayerSetup = {
    id: assetId,
    // YES, we need to pass both autoplay and autostart :(
    autostart,
    autoplay: autostart,
    preview: false,
    mute: playerMute,
    ...liveSetup,
    advRewarded: {
      initReplay: true,
      timer: {
        unit: 'minute',
        value: 10,
      },
      onAccept,
      onReject,
      onShow,
    },
    onEvents: {
      mute: ({
        payload: {
          event: { mute },
        },
      }: {
        payload: { event: { mute: boolean } };
      }) => {
        onTogglePlayerMute(mute);
      },
      ready: ({ event }: { event: string }) => {
        setEventName(event);
        onSetIsHiddenVideoInfoStatus(true);
        setVideoStarted(true);
      },
      adStarted: ({ event }: { event: string }) => {
        setShowCloseBtn(false);
        setEventName(event);
        onSetCategoryType('adv');
        onSetVideoStatusType('playing');
      },
      adCompleted: ({ event }: { event: string }) => {
        setEventName(event);
        onSetCategoryType('adv');
        onSetVideoStatusType('playing');
      },
      firstFrame: ({ event }: { event: string }) => setEventName(event),
      play: ({ event }: { event: string }) => {
        setEventName(event);
        setShowCloseBtn(true);
        onSetCategoryType('category');
        onSetVideoStatusType('playing');
        onSetIsHiddenVideoInfoStatus(false);
      },
      pause: ({ event }: { event: string }) => setEventName(event),
      complete: ({ event }: { event: string }) => {
        setEventName(event);
        onSetIsHiddenVideoInfoStatus(true);
      },
      SKY_PLAYER_DISPOSE: ({ event }: { event: string }) => {
        onSetIsHiddenVideoInfoStatus(true);
      },
    },
  };

  const onClose = () => {
    if (showCloseBtn) {
      setIsHiddenPoster(false);
      setPlayerReady(false);
      setIsSticky(false);
    }
  };
  return (
    <div className={styles('player-card')} ref={ref} id={assetId.toString()}>
      <PosterPlayer
        duration={duration}
        videoStill={videoStill}
        altImage={altImage}
        onClick={playButtonState === 'disabled' ? () => {} : () => handleClick(assetId.toString())}
        isHidden={isHiddenPoster}
        playButtonState={playButtonState}
        title={title}
      />
      <div
        className={styles([
          isSticky && videoStarted && isHiddenPoster && !navFilterallVisibility && 'player-card__sticky',
        ])}
      >
        {playerReady && <Player setup={playerSetup} eventName={eventName} clickedButton={isHiddenPoster} />}
        {!navFilterallVisibility && isSticky && playerReady && videoStarted && (
          <StickyPlayerHeader
            isDesktop={isDesktop}
            title={title.toUpperCase()}
            showCloseBtn={showCloseBtn}
            onClickClose={() => onClose()}
          />
        )}
      </div>
    </div>
  );
};

export default PlayerCard;
